import { io } from "socket.io-client";
import { DecryptData } from "../util/DataEncryption";
import { getSocketConnectionConfig } from "../util/socketConfig";
import { normalizeResultsSummary } from "./PollingAPI";

function readAuthToken() {
  const encrypted = sessionStorage.getItem("token");
  return encrypted ? DecryptData(encrypted) : null;
}

/**
 * Socket.IO client for live poll result broadcasts.
 * Browsers send the JWT in handshake.auth (see connect() below).
 */
class PollResultsSocketService {
  constructor() {
    this.socket = null;
    this.subscription = null;
    this.resultListeners = new Set();
    this.statusListeners = new Set();
    this.connectionStatus = "idle";
  }

  notifyStatus(status) {
    this.connectionStatus = status;
    this.statusListeners.forEach((listener) => listener(status));
  }

  resetSocket() {
    if (this.socket) {
      this.socket.removeAllListeners();
      this.socket.io?.removeAllListeners();
      this.socket.disconnect();
      this.socket = null;
    }
  }

  connect() {
    const token = readAuthToken();
    const { url: baseUrl, path: socketPath } = getSocketConnectionConfig();

    if (!token || !baseUrl) {
      this.notifyStatus("unavailable");
      return null;
    }

    if (this.socket?.connected) {
      this.socket.auth = { token };
      return this.socket;
    }

    if (this.socket) {
      this.resetSocket();
    }

    this.notifyStatus("connecting");

    // Browsers only send credentials via the Socket.IO `auth` payload (not custom
    // headers). The backend must read handshake.auth.token.
    this.socket = io(baseUrl, {
      path: socketPath,
      transports: ["polling", "websocket"],
      withCredentials: true,
      reconnection: true,
      reconnectionAttempts: 8,
      reconnectionDelay: 1500,
      auth: {
        token,
      },
    });

    this.socket.on("connect", () => {
      this.notifyStatus("connected");
      this.emitJoin();
    });

    this.socket.io.on("reconnect", () => {
      this.emitJoin();
    });

    this.socket.on("disconnect", (reason) => {
      if (reason === "io client disconnect") {
        return;
      }
      this.notifyStatus("disconnected");
    });

    this.socket.on("connect_error", (error) => {
      if (process.env.NODE_ENV === "development") {
        console.warn("[poll socket] connect_error:", error?.message || error);
      }
      this.notifyStatus("error");
    });

    this.socket.on("poll:results", (payload) => {
      if (
        this.subscription?.pollId &&
        payload?.pollId &&
        String(payload.pollId) !== String(this.subscription.pollId)
      ) {
        return;
      }

      const summary = normalizeResultsSummary(payload);
      if (!summary) {
        return;
      }

      this.resultListeners.forEach((listener) => listener(summary, payload));
    });

    return this.socket;
  }

  emitJoin() {
    if (!this.socket?.connected || !this.subscription) {
      return;
    }

    const { visitId, pollId } = this.subscription;
    this.socket.emit("poll:subscribe", pollId);
    this.socket.emit("poll:results:join", { visitId, pollId });
  }

  emitLeave() {
    if (!this.socket || !this.subscription) {
      return;
    }

    const { visitId, pollId } = this.subscription;
    this.socket.emit("poll:unsubscribe", pollId);
    this.socket.emit("poll:results:leave", { visitId, pollId });
  }

  subscribe(visitId, pollId) {
    const nextSubscription =
      visitId && pollId
        ? { visitId: String(visitId), pollId: String(pollId) }
        : null;

    if (!nextSubscription) {
      this.unsubscribe();
      return;
    }

    const unchanged =
      this.subscription?.pollId === nextSubscription.pollId &&
      this.subscription?.visitId === nextSubscription.visitId &&
      this.socket?.connected;

    if (unchanged) {
      return;
    }

    const socket = this.connect();
    if (!socket) {
      return;
    }

    if (this.subscription?.pollId) {
      this.emitLeave();
    }

    this.subscription = nextSubscription;

    if (socket.connected) {
      this.emitJoin();
    }
  }

  unsubscribe() {
    this.emitLeave();
    this.subscription = null;
  }

  onResults(listener) {
    this.resultListeners.add(listener);
    return () => {
      this.resultListeners.delete(listener);
    };
  }

  onStatus(listener) {
    this.statusListeners.add(listener);
    if (this.connectionStatus !== "idle") {
      listener(this.connectionStatus);
    }
    return () => {
      this.statusListeners.delete(listener);
    };
  }

  getStatus() {
    return this.connectionStatus;
  }

  disconnect() {
    this.unsubscribe();
    this.resetSocket();
    this.notifyStatus("idle");
  }
}

const pollResultsSocket = new PollResultsSocketService();

export default pollResultsSocket;
